"use client";

import { IconCheckOutline18, IconPencilOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { useCallback, useEffect, useMemo, useState } from "react";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { TagSelector } from "@/components/tags/TagSelector";
import { Button } from "@/components/ui/button";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useTemplatesStore, type Tag, type Template } from "@/stores/templatesStore";
interface EditableTemplateCategoriesProps {
  template: Template;
  canEdit: boolean;
}
const isCategoryTag = (tag: Tag) => !tag.imageUrl;
export function EditableTemplateCategories({
  template,
  canEdit
}: EditableTemplateCategoriesProps) {
  const updateTemplate = useTemplatesStore(state => state.updateTemplate);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const [selectedTagObjects, setSelectedTagObjects] = useState<Tag[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    categoryTagsOnTemplate,
    appTagIds
  } = useMemo(() => {
    const cats: Tag[] = [];
    const appIds: string[] = [];
    for (const tag of template.tags ?? []) {
      if (isCategoryTag(tag)) cats.push(tag);else appIds.push(tag.id);
    }
    return {
      categoryTagsOnTemplate: cats,
      appTagIds: appIds
    };
  }, [template.tags]);
  useEffect(() => {
    if (!isOpen) {
      setSelectedSlugs(categoryTagsOnTemplate.map(t => t.slug));
      setSelectedTagObjects(categoryTagsOnTemplate);
      setError(null);
    }
  }, [isOpen, categoryTagsOnTemplate]);
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    try {
      const nextTagIds = [...new Set([...appTagIds, ...selectedTagObjects.map(t_0 => t_0.id)])];
      await updateTemplate(template.id, {
        tags: nextTagIds
      });
      setIsOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  }, [appTagIds, selectedTagObjects, template.id, updateTemplate]);
  if (categoryTagsOnTemplate.length === 0 && !canEdit) return null;
  return <>
      <div className="flex items-start gap-2">
        <div className="flex flex-1 flex-wrap items-center gap-1">
          {categoryTagsOnTemplate.length > 0 ? categoryTagsOnTemplate.slice(0, 6).map(tag_0 => <span key={tag_0.id} className="bg-ds-surface-secondary text-ds-text-secondary rounded border border-black/10 px-1.5 py-0.5 text-xs font-medium">
                {tag_0.name}
              </span>) : <span className="text-ds-text-disabled text-xs italic">No categories</span>}
          {canEdit && <Button variant="ghost" size="xs" iconOnly onClick={() => setIsOpen(true)} title="Edit categories" aria-label="Edit categories" className="h-5 w-5">
              <IconPencilOutline18 className="h-3 w-3" />
            </Button>}
        </div>
      </div>

      <ResponsiveModal open={isOpen} onOpenChange={open => !isSaving ? setIsOpen(open) : undefined} title="Edit categories" contentClass="max-w-2xl" innerClass="bg-ds-surface-card rounded-ds-lg relative flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-black/5 px-6 py-4">
          <h2 className="text-content-secondary text-base font-semibold">Edit categories</h2>
        </div>
        <div className="overflow-y-auto px-6 py-4">
          <TagSelector selectedTags={selectedSlugs} onSelectionChange={setSelectedSlugs} onTagsChange={setSelectedTagObjects} initialTagObjects={categoryTagsOnTemplate} scope="templates_public" variant="flow" showImages={false} showCount={false} showSearch showPagination showCategoryFilter tagsPerPage={30} label="" filter={isCategoryTag} />
          {error && <p className="text-2-5xs mt-3 text-red-600">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-black/5 px-6 py-4">
          <Button variant="white" size="sm" onClick={() => setIsOpen(false)} disabled={isSaving}>
            <IconXmarkOutline18 className="h-3.5 w-3.5" />
            Cancel
          </Button>
          <Button variant="brand" size="sm" onClick={() => void handleSave()} disabled={isSaving}>
            {isSaving ? <PixelLoader size="xs" /> : <IconCheckOutline18 className="h-3.5 w-3.5" />}
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </ResponsiveModal>
    </>;
}